import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon } from 'lucide-react';
import { useProjects, useAuth, useTemplateCreation, useInstantMock } from '../hooks';
import { ProjectList, ProjectForm, ProjectsSection } from '../components/project';
import { TemplateSelector } from '../components/onboarding';
import { Alert, Modal, SessionUnavailable } from '../components/common';
import { Skeleton } from '../components/Skeleton';
import { getApiBaseUrl } from '../config';
import { TIER_LIMITS } from '@mockd/shared';
import type { Project, ProjectTemplate } from '@mockd/shared';

export function Dashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading, sessionError } = useAuth();
  const {
    projects,
    loading,
    error,
    fetchProjects,
    fetchAnonymousProjects,
    createProject,
    clearProjects,
  } = useProjects();
  const { createFromTemplate, isCreating: isTemplateCreating, loadingTemplateId } = useTemplateCreation();
  const [isCreating, setIsCreating] = useState(false);
  const [endpointCounts, setEndpointCounts] = useState<Record<string, number>>({});
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [anonymousProjects, setAnonymousProjects] = useState<Project[]>([]);
  const addAnonymousProject = useCallback((project: Project) => {
    setAnonymousProjects(prev => [...prev, project]);
  }, []);
  const { createInstantMock: handleQuickStart, isCreating: isQuickStarting } = useInstantMock({
    onProjectCreated: addAnonymousProject,
  });

  // Redirect unauthenticated users to landing — but not when the session check
  // failed, which means the API is down rather than that the user signed out.
  useEffect(() => {
    if (!authLoading && !user && !sessionError) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, sessionError, navigate]);

  const fetchEndpointCounts = useCallback(async (projectIds: string[]) => {
    const counts: Record<string, number> = {};
    await Promise.all(
      projectIds.map(async (projectId) => {
        try {
          const response = await fetch(`${getApiBaseUrl()}/api/projects/${projectId}/endpoints`, {
            credentials: 'include',
          });
          if (response.ok) {
            const json = await response.json();
            counts[projectId] = (json.data || []).length;
          }
        } catch {
          // Leave the count unknown — the card omits it rather than showing 0
        }
      })
    );
    setEndpointCounts(counts);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const load = async () => {
      const anon = await fetchAnonymousProjects();
      setAnonymousProjects(anon);
    };
    load();
  }, [fetchAnonymousProjects]);

  useEffect(() => {
    if (!user && !authLoading && !sessionError) {
      clearProjects();
    }
  }, [user, authLoading, sessionError, clearProjects]);

  const allProjects = [...projects, ...anonymousProjects];

  useEffect(() => {
    const allIds = [...projects.map(p => p.id), ...anonymousProjects.map(p => p.id)];
    if (allIds.length > 0) {
      fetchEndpointCounts(allIds);
    }
  }, [projects, anonymousProjects, fetchEndpointCounts]);

  const handleCreateProject = async (data: { name: string; subdomain: string }) => {
    setIsCreating(true);
    try {
      const project = await createProject(data);
      setShowNewProjectModal(false);
      navigate(`/projects/${project.id}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleTemplateSelect = async (template: ProjectTemplate) => {
    try {
      const project = await createFromTemplate(template);
      navigate(`/projects/${project.id}?from=template`);
    } catch {
      // Error handled by hook
    }
  };

  const hasProjects = allProjects.length > 0;
  const stillLoading = loading || authLoading;
  const busy = isCreating || isQuickStarting || isTemplateCreating;

  if (!authLoading && sessionError) {
    return <SessionUnavailable />;
  }

  return (
    <div className="min-h-screen bg-base-200">
      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <Modal
          isOpen={showNewProjectModal}
          onClose={() => setShowNewProjectModal(false)}
          title="New Project"
          boxClassName="max-w-4xl"
          showCloseButton
        >
          <ProjectForm
            onSubmit={handleCreateProject}
            onCancel={() => setShowNewProjectModal(false)}
            isLoading={isCreating}
          />
        </Modal>

        {error && (
          <Alert variant="error" className="mb-8">{error}</Alert>
        )}

        {stillLoading && allProjects.length === 0 ? (
          <Skeleton variant="project-list" />
        ) : hasProjects ? (
          user ? (
            <>
              {/*
                Saved projects only: the limit counts the personal bucket the server
                caps, which temporary (anonymous) projects don't belong to (#260).
              */}
              <ProjectsSection
                headingLevel="h1"
                projects={projects}
                endpointCounts={endpointCounts}
                endpointLimit={user.tier === 'free' ? TIER_LIMITS.free.endpointsPerProject : undefined}
                canCreate
                onCreateClick={() => setShowNewProjectModal(true)}
                isCreating={busy}
                limit={user.tier === 'free' ? { used: projects.length, max: TIER_LIMITS.free.projects, label: 'Projects' } : undefined}
                headerActions={
                  <div className="dropdown dropdown-end">
                    <div
                      tabIndex={0}
                      role="button"
                      className="btn btn-primary btn-sm join-item"
                      aria-label="More create options"
                    >
                      <ChevronDownIcon className="size-4" />
                    </div>
                    <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 bg-base-100 border border-base-300 rounded-box w-56">
                      <li>
                        <button
                          onClick={handleQuickStart}
                          disabled={busy}
                          className="flex flex-col items-start"
                        >
                          <span>Instant Mock</span>
                          <span className="text-xs text-base-content/60">Instant throwaway endpoint</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                }
              />
              {anonymousProjects.length > 0 && (
                <div className="mt-8">
                  <h2 className="text-sm font-medium text-base-content/70 mb-3">Temporary projects</h2>
                  {/* Anonymous projects always run on Free limits, whatever the viewer's plan. */}
                  <ProjectList
                    projects={anonymousProjects}
                    endpointCounts={endpointCounts}
                    endpointLimit={TIER_LIMITS.free.endpointsPerProject}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <h1>Projects</h1>
                <button
                  onClick={handleQuickStart}
                  disabled={busy}
                  className="btn btn-outline btn-sm"
                  title="Create a temporary, anonymous mock endpoint — no account needed"
                >
                  Instant Mock
                </button>
              </div>
              <ProjectList projects={allProjects} endpointCounts={endpointCounts} />
            </>
          )
        ) : (
          <div className="max-w-5xl mx-auto py-8">
            <div className="text-center mb-8">
              <h1 className="text-base-content">Get started with mockd</h1>
              <p className="text-base text-base-content/70 mt-2">
                Pick a template to spin up a working mock in one click.
              </p>
            </div>
            <TemplateSelector
              size="hero"
              onSelect={handleTemplateSelect}
              isLoading={isTemplateCreating}
              loadingTemplateId={loadingTemplateId}
            />
            <p className="text-center text-sm text-base-content/60 mt-8">
              Prefer to start from scratch?{' '}
              <button
                onClick={() => setShowNewProjectModal(true)}
                disabled={busy}
                className="link link-primary"
              >
                Create a blank project
              </button>
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
